import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import { CONDENSED_STACK } from './tokens';

const COLOR_HEAT = '#e81a17';
const COLOR_BUZZ = '#0077ff';

// Lifts the badge off whatever image it lands on.
const PLATE_SX = {
  position: 'absolute',
  bottom: 6,
  right: 6,
  background: 'rgb(255, 255, 255)',
  borderRadius: '4px',
  padding: '3px 6px',
  zIndex: 2,
};

const BADGE_STYLE = {
  borderRadius: 4,
  overflow: 'hidden',
  display: 'inline-flex',
  alignItems: 'stretch',
  background: 'transparent',
  fontFamily: CONDENSED_STACK,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '0.4px',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  border: 0,
  padding: 0,
};

const SEGMENT_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 1px',
};

function Fire() {
  return (
    <svg width="11" height="12.8" viewBox="0 0 12 14" style={{ display: 'block', flexShrink: 0 }}>
      <path
        d="M6 0.5 C 7.5 3.6, 11 5.4, 11 8.9 C 11 11.8, 8.7 13.4, 6 13.4 C 3.3 13.4, 1 11.9, 1 8.9 C 1 6.4, 3 5.9, 4 3.9 C 4.4 5.3, 4.7 5.9, 6 5.4 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Bolt() {
  return (
    <svg width="11" height="12.8" viewBox="0 0 12 14" style={{ display: 'block', flexShrink: 0 }}>
      <path
        d="M7.4 0.6 L 1.5 8.0 L 5.4 8.0 L 4.0 13.4 L 10.5 5.6 L 6.4 5.6 L 7.4 0.6 Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * An item's, author's or site's heat and buzz, pinned to the bottom right of
 * the positioned frame it sits in. Without `onClick` a click passes through to
 * that frame.
 */
function HeatBuzzBadge({ heat, buzz, onClick }) {
  const showH = heat != null && heat > 0;
  const showB = buzz != null && buzz > 0;
  if (!showH && !showB) return null;

  const interactive = typeof onClick === 'function';
  const style = interactive
    ? { ...BADGE_STYLE, cursor: 'pointer' }
    : BADGE_STYLE;

  const content = (
    <>
      {showH && (
        <span style={{ ...SEGMENT_STYLE, color: COLOR_HEAT }}>
          <Fire />
          <span style={{ fontWeight: 900 }}>{Math.round(heat)}</span>
        </span>
      )}
      {showB && (
        <span style={{ ...SEGMENT_STYLE, color: COLOR_BUZZ }}>
          <Bolt />
          <span style={{ fontWeight: 900 }}>{Math.round(buzz)}</span>
        </span>
      )}
    </>
  );

  return (
    <Box sx={PLATE_SX} onClick={interactive ? (e) => e.stopPropagation() : undefined}>
      {interactive ? (
        <button
          type="button"
          style={style}
          onClick={(e) => { e.stopPropagation(); onClick(e); }}
        >
          {content}
        </button>
      ) : <div style={style}>{content}</div>}
    </Box>
  );
}

HeatBuzzBadge.propTypes = {
  heat: PropTypes.number,
  buzz: PropTypes.number,
  onClick: PropTypes.func,
};

HeatBuzzBadge.defaultProps = {
  heat: null,
  buzz: null,
  onClick: null,
};

export default HeatBuzzBadge;
